/**
 * Обычный маршрут в обход опасностей (City Resilience Hack, 24.09.2026).
 *
 * planRoute блокировок не знает, поэтому ему отдаётся граф без перекрытого
 * (filterGraph). Сначала считаем кратчайший путь по полному графу: если он
 * опасность не задевает — он и есть ответ (ни лишней работы, ни ложного
 * «в обход»). Задевает — считаем безопасный и честно говорим, сколько метров
 * добавил обход, или что к цели не пройти.
 *
 * «Не пройти» — не только когда сеть оборвана: planRoute умеет фолбэк «по сети
 * докуда можно + пунктир к двери», и отфильтрованный граф превращал бы
 * перекрытый коридор в пунктир СКВОЗЬ огонь. Поэтому пунктир, который вырос
 * или пересёк опасность, тоже считается «не пройти».
 */
import {
  accessVerified,
  type BuiltGraph,
  planRoute,
  type RoutePlan,
  type RouteProfile,
  type RouteStart,
  type Venue,
} from '@app/rvf'
import { activeHazards, graph, hazardBlocks, liveHazardUnits } from '../state'
import { planCrossesLevels } from '../ui/labels'
import type { PointHazard, RouteDetour } from './alert-types'
import {
  type BlockCause,
  detourExtraM,
  filterGraph,
  firstCause,
  type HazardBlocks,
  hasBlocks,
  hazardAt,
  segmentHitsHazard,
  zoneAround,
} from './hazards'

/** Пунктир к двери мог удлиниться на округления проекции — не больше этого, м. */
const EGRESS_SLACK_M = 5

export interface HazardContext {
  readonly blocks: HazardBlocks
  readonly hazards: readonly PointHazard[]
  /** Горящие помещения тревоги — цели в них недостижимы. */
  readonly units: readonly string[]
}

export interface SafePlan {
  readonly plan: RoutePlan | null
  /** Обход или «не пройти» (unreachable); null — кратчайший путь чист. */
  readonly detour: RouteDetour | null
}

const NO_PLAN: SafePlan = { plan: null, detour: null }

function levelOf(v: Venue, id: string): string | null {
  return (v.units.find((u) => u.id === id) ?? v.pois.find((p) => p.id === id))?.level ?? null
}

function lengthM(seg: readonly (readonly [number, number])[]): number {
  const a = seg[0]
  const b = seg[1]
  if (!a || !b) return 0
  const cos = Math.cos(a[1] * (Math.PI / 180))
  return Math.hypot((b[0] - a[0]) * 111_320 * cos, (b[1] - a[1]) * 110_540)
}

/** Пунктир к двери плана пересекает опасность на этаже цели. */
function egressHazard(plan: RoutePlan, level: string | null, hazards: readonly PointHazard[]) {
  const [a, b] = plan.egress
  return a && b ? segmentHitsHazard(a, b, level, hazards) : null
}

/** Цель сама в опасности: горящее помещение или дверь в круге опасности. */
function targetCause(v: Venue, plan: RoutePlan, id: string, ctx: HazardContext): BlockCause | null {
  const level = levelOf(v, id)
  if (ctx.units.includes(id)) return { kind: 'units', levelId: level }
  const end = plan.polyline[plan.polyline.length - 1]
  const h = end ? hazardAt(end, level, ctx.hazards) : null
  return h && h.kind !== 'smoke' ? { kind: h.kind, levelId: h.level } : null
}

function unreachable(cause: BlockCause): SafePlan {
  return {
    plan: null,
    detour: { kind: cause.kind, levelId: cause.levelId, extraM: 0, unreachable: true },
  }
}

/**
 * Маршрут к цели в обход опасностей (чистая функция: всё нужное — параметрами).
 * Старт внутри опасности не запирает человека: зона, где он стоит, остаётся
 * проходимой — из неё выводим.
 */
export function planAround(
  v: Venue,
  full: BuiltGraph,
  ctx: HazardContext,
  start: RouteStart,
  id: string,
  profile: RouteProfile = 'foot',
): SafePlan {
  const direct = planRoute(v, full, start, id, profile)
  if (!direct) return NO_PLAN // недостижимо и без опасностей — не наша причина
  if (!hasBlocks(ctx.blocks) && ctx.hazards.length === 0) return { plan: direct, detour: null }
  const inTarget = targetCause(v, direct, id, ctx)
  if (inTarget) return unreachable(inTarget)
  const level = levelOf(v, id)
  const zone = zoneAround(full, ctx.blocks, start.nodeId)
  const onPath = firstCause(
    direct.nodeIds.filter((n) => !zone.has(n)),
    ctx.blocks,
  )
  const throughEgress = egressHazard(direct, level, ctx.hazards)
  if (!onPath && !throughEgress) return { plan: direct, detour: null }
  const cause: BlockCause = onPath ?? {
    kind: (throughEgress as PointHazard).kind,
    levelId: (throughEgress as PointHazard).level,
  }
  const safe = planRoute(v, filterGraph(full, ctx.blocks, zone), start, id, profile)
  if (
    !safe ||
    lengthM(safe.egress) > lengthM(direct.egress) + EGRESS_SLACK_M ||
    egressHazard(safe, level, ctx.hazards)
  ) {
    return unreachable(cause)
  }
  return {
    plan: safe,
    detour: {
      kind: cause.kind,
      levelId: cause.levelId,
      extraM: detourExtraM(direct.distanceM, safe.distanceM),
    },
  }
}

/** Опасности вьюера сейчас (сигналы state.ts). */
export function hazardContext(): HazardContext {
  return { blocks: hazardBlocks.value, hazards: activeHazards.value, units: liveHazardUnits.value }
}

/** planAround на текущем графе и опасностях вьюера. */
export function planWithHazards(
  v: Venue,
  start: RouteStart,
  id: string,
  profile: RouteProfile = 'foot',
): SafePlan {
  const g = graph.value
  return g ? planAround(v, g, hazardContext(), start, id, profile) : NO_PLAN
}

/**
 * Чип «Можно без лестниц»: только когда маршрут реально меняет этаж и
 * доступность размечена (иначе обещали бы путь по непроверенным данным —
 * замечание эксперта по доступности, 29.07). Путь без лестниц тоже обходит
 * опасности. null — чип не показываем.
 */
export function stepFreeFor(
  v: Venue,
  start: RouteStart | null,
  id: string,
  plan: RoutePlan | null,
): boolean | null {
  if (!start || !plan || !planCrossesLevels(v, plan) || !accessVerified(v.network.edges)) {
    return null
  }
  return planWithHazards(v, start, id, 'wheelchair').plan !== null
}
